import { useEffect, useRef } from "react";

type Shortcuts = {
  enabled: boolean;
  hasSelection: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onRotate: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onDeselect: () => void;
};

const isTypingTarget = (target: EventTarget | null) => {
  const element = target as HTMLElement | null;
  const tag = element?.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || !!element?.isContentEditable;
};

/**
 * Буквата на клавиша, независимо от подредбата: при латиница — `event.key`
 * (AZERTY/QWERTZ пазят своите букви), при кирилица (`key` е „р", „я"…) —
 * физическият клавиш от `event.code` („KeyR" → "r").
 */
export const shortcutLetter = (event: { key: string; code: string }): string => {
  const key = event.key.toLowerCase();
  if (/^[a-z]$/.test(key)) return key;
  return /^Key[A-Z]$/.test(event.code) ? event.code.slice(3).toLowerCase() : key;
};

/**
 * Клавишите на редактора (подсказките в дизайна): ⌘Z / Ctrl+Z отмени,
 * ⇧⌘Z / Ctrl+Y повтори, R завърти, ⌘D дублирай, Del/Backspace изтрий,
 * Esc маха избора. Всеки обработен клавиш вика preventDefault — иначе Esc
 * минава и през глобалната навигация „назад", а ⌘D отваря отметките.
 * Изключени са, докато пишеш в поле или е отворен модал (`enabled`).
 * Работят и при българска клавиатура (`shortcutLetter`).
 */
export const useEditorShortcuts = (shortcuts: Shortcuts) => {
  const latest = useRef(shortcuts);
  latest.current = shortcuts;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const current = latest.current;
      if (!current.enabled || isTypingTarget(event.target)) return;
      const withModifier = event.metaKey || event.ctrlKey;
      const key = shortcutLetter(event);

      if (withModifier && key === "z") {
        event.preventDefault();
        if (event.shiftKey) current.onRedo();
        else current.onUndo();
        return;
      }
      if (withModifier && key === "y") {
        event.preventDefault();
        current.onRedo();
        return;
      }
      if (!current.hasSelection) return;
      if (withModifier && key === "d") {
        event.preventDefault();
        current.onDuplicate();
      } else if (!withModifier && key === "r") {
        event.preventDefault();
        current.onRotate();
      } else if (event.key === "Delete" || event.key === "Backspace") {
        event.preventDefault();
        current.onDelete();
      } else if (event.key === "Escape") {
        event.preventDefault();
        current.onDeselect();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);
};
